import {
  ListItemButton,
  ListItem as MuiListItem,
  ListItemTypeMap as MuiListItemTypeMap,
  ListItemProps as MuiListItemProps,
  listItemSecondaryActionClasses,
  useThemeProps,
} from '@mui/material';
import { OverrideProps } from '@mui/material/OverridableComponent';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import React from 'react';
import { AnyProps } from 'src/types/util';
import { globalMuiClasses } from 'src/utils/cssSelectorConstants';
import {
  ExtendAndOmitTypeMap,
  forwardRefToOverridableComponent,
} from 'src/utils/forwardRefToOverridableComponent';

export interface ListItemOwnProps {
  /**
   * @deprecated
   * Use `ListItemButton` instead.
   */
  autoFocus?: boolean;

  /**
   * @deprecated
   * Use `ListItemButton` instead.
   */
  disabled?: boolean;

  /**
   * @deprecated
   * Use `ListItemButton` instead.
   */
  selected?: boolean;

  /**
   * @deprecated
   * Use `secondaryActions` instead.
   */
  secondaryAction?: React.ReactNode;

  /**
   * The element(s) to display at the end of ListItem.
   * When multiple actions are present, wrap them with a `ListItemSecondaryActions` component.
   */
  secondaryActions?: React.ReactNode;
}

/**
 * Deprecate props that aren't supported in Unify.
 */
type UnsupportedProps = {
  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  divider?: MuiListItemProps['divider'];
};

type ListItemDefaultComponent = 'li';

export type ListItemTypeMap<
  AdditionalProps = unknown,
  RootComponent extends React.ElementType = MuiListItemTypeMap<
    AdditionalProps,
    ListItemDefaultComponent
  >['defaultComponent']
> = ExtendAndOmitTypeMap<
  MuiListItemTypeMap<AdditionalProps, ListItemDefaultComponent>,
  AdditionalProps & ListItemOwnProps & UnsupportedProps,
  keyof UnsupportedProps | 'focusRipple',
  RootComponent
>;

export type ListItemProps<
  RootComponent extends React.ElementType = MuiListItemTypeMap<
    AnyProps,
    ListItemDefaultComponent
  >['defaultComponent'],
  AdditionalProps = unknown
> = OverrideProps<
  ListItemTypeMap<AdditionalProps, RootComponent>,
  RootComponent
>;

const ListItemSecondaryActionsWrapper = styled('div', {
  name: 'ListItemSecondaryActionsWrapper',
  slot: 'Root',
})(({ theme }) => ({
  [`.${listItemSecondaryActionClasses.root}`]: {
    marginLeft: theme.spacing(1),
    position: 'static',
    transform: 'none',
  },
}));

export const ListItem = forwardRefToOverridableComponent<
  ListItemTypeMap,
  ListItemProps
>(function ListItem(inProps, forwardedRef) {
  const props = useThemeProps({ props: inProps, name: 'MuiListItem' });

  const {
    children,
    secondaryActions,
    className: classNameProp,
    // eslint-disable-next-line deprecation/deprecation
    selected,
    ...other
  } = props;

  const className = clsx(classNameProp, {
    [globalMuiClasses.selected]: selected,
  });
  const secondaryActionsElement = secondaryActions ? (
    <ListItemSecondaryActionsWrapper
      className={listItemSecondaryActionClasses.root}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      onTouchStart={(e) => {
        e.stopPropagation();
      }}
    >
      {secondaryActions}
    </ListItemSecondaryActionsWrapper>
  ) : (
    <></>
  );
  // Check if there is a ListItemButton among the children
  let hasListItemButton = false;

  // If there is a ListItemButton, we need to render it and inject the secondaryActions into it
  const renderedChildren = secondaryActions
    ? React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === ListItemButton) {
          hasListItemButton = true;
          return React.cloneElement(
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            child as React.ReactElement, // Cast to ReactElement for 'role' prop
            {
              role:
                // Remove button role for a11y - can't have a button within a button
                child.props.role === undefined || child.props.role === 'button'
                  ? undefined
                  : child.props.role,
            },
            child.props.children,
            secondaryActionsElement
          );
        }
        return child;
      })
    : children;

  return (
    <MuiListItem ref={forwardedRef} className={className} {...other}>
      {renderedChildren}
      {!hasListItemButton ? secondaryActionsElement : null}
    </MuiListItem>
  );
});

export default ListItem;
